import React, { memo, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useGetLastLoginQuery, useGetUserProfileQuery } from "@api/accountManagement";
import styled from "styled-components";
import { BettoCard } from "../../../components/BettoCard";
import { BettoCardGrid } from "../../../shared/elements";
import { BettoSecurityCard } from "../../../components/BettoSecurityCard";
import { BTTO_PROFILE_COLORS } from "../../../shared/elements";
import { BettoChangePassword } from "../../../components/BettoChangePassword";
import { BettoTwoFactorAuth } from "../../../components/BettoTwoFactorAuth";
import { BettoSelfExclusion } from "../../../components/BettoSelfExclusion";

const SecurityHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 20px;
`;

const SecurityTitle = styled.span`
	color: ${BTTO_PROFILE_COLORS.text};
	font-size: 16px;
	font-weight: 600;
`;

const SecurityBadge = styled.span`
	display: inline-flex;
	padding: 4px 10px;
	border-radius: 6px;
	background: rgba(239, 68, 68, 0.15);
	color: ${BTTO_PROFILE_COLORS.danger};
	font-size: 12px;
	font-weight: 600;
`;

const SessionsTable = styled.div`
	display: flex;
	flex-direction: column;
	overflow-x: auto;
`;

const SessionRow = styled.div`
	display: grid;
	grid-template-columns: 1.5fr 0.5fr 1.5fr 1fr 0.8fr;
	gap: 12px;
	padding: 12px 0;
	border-bottom: 1px solid ${BTTO_PROFILE_COLORS.border};
	font-size: 12px;
	color: ${BTTO_PROFILE_COLORS.textMuted};

	&:last-child {
		border-bottom: none;
	}

	@media (max-width: 767px) {
		grid-template-columns: 1fr;
		gap: 4px;
		padding: 16px 0;
	}
`;

const SessionHeader = styled(SessionRow)`
	font-weight: 600;
	color: ${BTTO_PROFILE_COLORS.text};
	padding-bottom: 8px;
`;

const SessionCell = styled.span`
	color: ${BTTO_PROFILE_COLORS.text};
`;

const OnlineBadge = styled.span`
	color: ${BTTO_PROFILE_COLORS.success};
	font-weight: 600;
`;

const LockIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
		<rect x="5" y="11" width="14" height="10" rx="2" />
		<path d="M8 11V8a4 4 0 018 0v3" />
	</svg>
);

const MailIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
		<rect x="3" y="5" width="18" height="14" rx="2" />
		<path d="M3 10l9 6 9-6" />
	</svg>
);

const PhoneIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
		<rect x="7" y="2" width="10" height="20" rx="2" />
		<circle cx="12" cy="18" r="1" fill="currentColor" />
	</svg>
);

const ShieldIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
		<path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />
	</svg>
);

export const SecuritySection = memo(({ translate, user, toggleLoading }) => {
	const [activePanel, setActivePanel] = useState(null);
	const { data: lastLoginData } = useGetLastLoginQuery();
	const { data: userInfo } = useGetUserProfileQuery();

	const hasEmail = Boolean(userInfo?.email || user?.email);
	const hasPhone = Boolean(userInfo?.phone_number || user?.phone_number);
	const hasMfa = Boolean(user?.mfa_enabled);

	const securityScore = useMemo(() => {
		let score = 0;
		if (hasEmail) score += 1;
		if (hasPhone) score += 1;
		if (hasMfa) score += 2;
		return score;
	}, [hasEmail, hasPhone, hasMfa]);

	const isWeak = securityScore < 3;

	const handlePasswordClick = useCallback(() => {
		setActivePanel((prev) => (prev === "password" ? null : "password"));
	}, []);

	const handle2FAClick = useCallback(() => {
		setActivePanel((prev) => (prev === "2fa" ? null : "2fa"));
	}, []);

	const securityCards = useMemo(
		() => [
			{
				id: "password",
				icon: <LockIcon />,
				title: translate("Change Password"),
				description: translate("betto.profile.passwordDesc"),
				isComplete: true,
				actionLabel: translate("Change Password"),
				onAction: handlePasswordClick,
				isActionDisabled: false,
			},
			{
				id: "email",
				icon: <MailIcon />,
				title: translate("betto.profile.emailVerification"),
				description: translate("betto.profile.emailVerificationDesc"),
				isComplete: hasEmail,
				actionLabel: hasEmail
					? translate("betto.profile.verified")
					: translate("betto.profile.verify"),
				onAction: () => {},
				isActionDisabled: hasEmail,
			},
			{
				id: "phone",
				icon: <PhoneIcon />,
				title: translate("betto.profile.phoneVerification"),
				description: translate("betto.profile.phoneVerificationDesc"),
				isComplete: hasPhone,
				actionLabel: hasPhone
					? translate("betto.profile.verified")
					: translate("betto.profile.verifyPhone"),
				onAction: () => {},
				isActionDisabled: hasPhone,
			},
			{
				id: "2fa",
				icon: <ShieldIcon />,
				title: translate("betto.profile.twoFactor"),
				description: translate("betto.profile.twoFactorDesc"),
				isComplete: hasMfa,
				actionLabel: hasMfa
					? translate("betto.profile.manage2fa")
					: translate("betto.profile.enable2fa"),
				onAction: handle2FAClick,
				isActionDisabled: false,
			},
		],
		[translate, hasEmail, hasPhone, hasMfa, handlePasswordClick, handle2FAClick]
	);

	return (
		<>
			<BettoCard>
				<SecurityHeader>
					<SecurityTitle>{translate("betto.profile.securitySetup")}</SecurityTitle>
					{isWeak && <SecurityBadge>{translate("betto.profile.weak")}</SecurityBadge>}
				</SecurityHeader>
				<BettoCardGrid>
					{securityCards.map((card) => (
						<BettoSecurityCard key={card.id} {...card} />
					))}
				</BettoCardGrid>
			</BettoCard>

			{activePanel === "password" && (
				<BettoCard title={translate("Change Password")}>
					<BettoChangePassword user={user} translate={translate} />
				</BettoCard>
			)}

			{activePanel === "2fa" && (
				<BettoCard title={translate("betto.profile.twoFactor")}>
					<BettoTwoFactorAuth
						translate={translate}
						toggleLoading={toggleLoading}
						enabled={hasMfa}
					/>
				</BettoCard>
			)}

			<BettoCard title={translate("betto.profile.selfExclusion")}>
				<BettoSelfExclusion translate={translate} />
			</BettoCard>

			<BettoCard title={translate("betto.profile.sessions")}>
				<SessionsTable>
					<SessionHeader>
						<span>{translate("betto.profile.device")}</span>
						<span>{translate("betto.profile.location")}</span>
						<span>{translate("IP Address")}</span>
						<span>{translate("betto.profile.lastUsed")}</span>
						<span>{translate("betto.profile.action")}</span>
					</SessionHeader>
					<SessionRow>
						<SessionCell>{translate("betto.profile.currentDevice")}</SessionCell>
						<SessionCell>-</SessionCell>
						<SessionCell>{lastLoginData?.lastLoginIp || "-"}</SessionCell>
						<OnlineBadge>{translate("betto.profile.online")}</OnlineBadge>
						<SessionCell>{translate("betto.profile.inUse")}</SessionCell>
					</SessionRow>
				</SessionsTable>
			</BettoCard>
		</>
	);
});

SecuritySection.displayName = "SecuritySection";

SecuritySection.propTypes = {
	translate: PropTypes.func.isRequired,
	user: PropTypes.object,
	toggleLoading: PropTypes.func,
};

const mapStateToProps = (state) => ({
	user: state.profile.user,
});

export default connect(mapStateToProps)(SecuritySection);
